import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import { UnsavedChangesGuard } from '@RichTextEditor';
import RichTextEditor from '@Components/UI/RichTextEditor/RichTextEditor';
import { useTypedSelector } from '@Features/store';
import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, InputAdornment } from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import { FlexLayout, Spacer } from '@Styled/utilities';
import React, { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { ContentConfig } from '../../../../Constants/CourseContent.constants';
import { CONTENT_SCHEMA } from '../../Schema/webContent.schema';
import { WebContentRequester } from '../../Services/WebContent.requester';
import { Content } from '../../Types/WebContent.types';

const EditWebContentInfo = () => {
	const editorRef = useRef<any>(null);
	const { displaySnackbar } = useSnackbar();
	const history = useHistory();

	const { currentEntity } = useTypedSelector((state) => state.WebContents);

	const mapEntityToForm = (entity: typeof currentEntity): Content => ({
		id: entity?.webContent?.id,
		unit: {
			name: entity?.unit?.name,
			course_id: entity?.unit?.course_id,
			order: entity?.unit?.order,
			type_id: entity?.unit?.type_id,
		},
		webContent: {
			content: entity?.webContent?.content,
		},
	});

	const { control, formState, handleSubmit, reset } = useForm<Content>({
		mode: 'all',
		resolver: yupResolver(CONTENT_SCHEMA),
		defaultValues: currentEntity ? mapEntityToForm(currentEntity) : undefined,
	});

	// Follow refetches and entity switches (Edit unit A -> Edit unit B):
	// the one-shot defaultValues above only cover the first mount.
	React.useEffect(() => {
		if (currentEntity) reset(mapEntityToForm(currentEntity));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentEntity?.webContent?.id]);

	const onSave = async (form: Content) => {
		try {
			const {
				data: { data },
			} = await WebContentRequester.instance.update({
				...form,
			});
			displaySnackbar('success', `Web Content updated successfully`);
			history.goBack();
		} catch (e) {
			const msg = e.response?.data.message || 'Unable to updated Web Content ';
			displaySnackbar('error', msg);
		}
	};

	return (
		<FlexLayout flexDirection="column" px="2.625rem" flex="1">
			<UnsavedChangesGuard
				formState={formState}
			/>
			<EdFormControl
				size="small"
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							{ContentConfig['webcontent'].icon}
						</InputAdornment>
					),
				}}
				control={control}
				label="Unit Name"
				name="unit.name"
			/>
			<Spacer mb="1.625rem" />
			<FlexLayout flex="1">
				<Controller
					control={control}
					name="webContent.content"
					render={({ field: { value, onChange, ...props } }) => {
						return (
							<RichTextEditor preset="full"
								{...props}
								value={value}
								onChange={onChange}
							/>
						);
					}}
				/>
			</FlexLayout>
			<FlexLayout mt="1.688rem" justifyContent="flex-end">
				<Button
					variant="contained"
					color="warning"
					onClick={() => history.goBack()}
				>
					Cancel
				</Button>
				<Spacer mx="0.5rem" />
				<Button
					onClick={handleSubmit(onSave)}
					disabled={
						!formState.isValid ||
						!formState.isDirty ||
						formState.isSubmitting
					}
					variant="contained"
				>
					Save
				</Button>
			</FlexLayout>
		</FlexLayout>
	);
};

export default EditWebContentInfo;
